import type { MouseEvent } from 'react'
import { legacyContractDataAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchDeskNavigation, type DeskViewMode } from '@/msgdeskRuntime/deskNavigation'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import { AuthEntry } from '@/deskUiSkeletons/Shared/AuthEntry'
import { DeskStateBadge } from '@/deskUiSkeletons/Shared/DeskStateBadge'
import { ParentsMenu } from '@/deskUiSkeletons/Menu/ParentsMenu'
import { useDeskMenu } from '@/deskUiSkeletons/Menu/menuModel'

// Forum wording for the shared menu-model entry vocabulary.
const labels: Record<DeskViewMode, string> = {
  msg: 'Topic',
  answers: 'Replies',
  submsges: 'Topics',
  attachments: 'Files',
  favs: 'Bookmarks',
  drafts: 'Drafts',
  trash: 'Basket',
}

export function ForumMenu({ isEditorOpen, editorMode, settingsOpen, onToggleSettings }: {
  isEditorOpen: boolean
  editorMode?: EditorMode
  settingsOpen: boolean
  onToggleSettings: () => void
}) {
  const menu = useDeskMenu()
  const canAddAnswer = menu.can.reply
  const canAddMessage = menu.can.createMessage
  // LOCKED-SCREEN: a show/time-locked desk shows ONLY the locked template.
  const locked = !menu.access.showUnlocked
  const mainEditUnsupported = editorMode === 'update_msg'
  const saveLabel = editorMode === 'new_answer' ? 'Submit reply' : editorMode === 'new_submsg' || editorMode === 'new_msg' ? 'Create topic' : 'Save'

  function navigate(event: MouseEvent<HTMLButtonElement>, mode: DeskViewMode) {
    dispatchDeskNavigation({ mode, target: event.currentTarget })
  }

  function save(event: MouseEvent<HTMLButtonElement>) {
    if (mainEditUnsupported) return
    dispatchEditorAction({ action: 'save', target: event.currentTarget, reason: 'forum-menu:before-save' })
  }

  const baseButton = 'h-7 flex-none border border-[#006699] px-2 text-[10px] font-bold'

  return (
    <nav {...legacyContractDataAttrs('sidebar.root')} className={`fixed inset-x-1 top-1 z-[100000] mx-auto flex h-9 max-w-[calc(100vw-8px)] items-center gap-1 overflow-x-auto border border-[#006699] bg-[#eef3f8] p-1 font-[Verdana,Arial,Helvetica,sans-serif] text-[10px] text-[#006699] lg:max-w-max${locked ? ' hidden' : ''}`} style={locked ? { display: 'none' } : undefined} aria-label="Forum controls">
      <span className="flex h-7 flex-none items-center bg-[#006699] px-3 font-bold text-white">FORUM</span>
      {isEditorOpen ? (
        <>
          <button type="button" className={`${baseButton} ${mainEditUnsupported ? 'cursor-not-allowed bg-[#d9d9d9] text-[#666]' : 'bg-[#006699] text-white hover:bg-[#004e77]'}`} disabled={mainEditUnsupported} title={mainEditUnsupported ? 'Editing the forum root is disabled until the save API can preserve msg_type=forum.' : undefined} onClick={save}>{mainEditUnsupported ? 'Root edit unavailable' : saveLabel}</button>
          <button type="button" className={`${baseButton} bg-white hover:bg-[#dfe6ef]`} aria-pressed={settingsOpen} onClick={onToggleSettings}>{settingsOpen ? 'Content' : 'Options'}</button>
          <button type="button" className={`${baseButton} bg-white text-[#8b0000] hover:bg-[#ffffcc]`} onClick={event => navigate(event, 'msg')}>Close</button>
        </>
      ) : (
        <>
          {/* FAVS-IDENTITY: no Bookmarks tab for anonymous viewers of an edit-locked board.
              §0.16: model-unavailable entries (Topics on an answer desk) never render. */}
          {menu.tabs.filter(tab => tab.available && tab.hasItems && (!tab.requiresEdit || menu.access.editUnlocked) && (tab.mode !== 'favs' || menu.can.favs)).map(tab => <button key={tab.mode} type="button" className={`${baseButton} ${tab.isActive ? 'bg-[#006699] text-white' : 'bg-white hover:bg-[#dfe6ef]'}`} aria-current={tab.isActive ? 'page' : undefined} onClick={event => navigate(event, tab.mode)}>{labels[tab.mode]}</button>)}
          {canAddAnswer ? <button type="button" className={`${baseButton} bg-[#ffffcc] hover:bg-[#fff1a6]`} onClick={() => dispatchEditorAction({ action: 'open-reply' })}>Post reply</button> : null}
          {canAddMessage ? <button type="button" className={`${baseButton} bg-[#ffffcc] hover:bg-[#fff1a6]`} onClick={() => dispatchEditorAction({ action: 'open-new-message' })}>New topic</button> : null}
          <button type="button" className={`${baseButton} bg-white hover:bg-[#dfe6ef]`} onClick={() => dispatchEditorAction({ action: 'open-edit-message' })}>Edit topic</button>
          {/* PARENTS-MENU: shared skeleton leaf, forum skin. The bar is
              overflow-x-auto, so the panel is viewport-fixed just under it
              (no backdrop-filter here, so fixed stays viewport-based) and
              mirrors the bar's inset-x-1 mx-auto centering. */}
          <ParentsMenu skin={{
            root: 'flex h-7 flex-none items-center',
            trigger: `${baseButton} flex cursor-pointer items-center gap-1 bg-white hover:bg-[#dfe6ef]`,
            triggerOpen: 'bg-[#dfe6ef]',
            count: 'font-normal',
            list: 'fixed inset-x-1 top-[37px] z-[100001] mx-auto flex max-w-max flex-col gap-0.5 border border-[#006699] bg-[#eef3f8] p-2 shadow-lg',
            link: 'whitespace-nowrap px-2 py-1 text-[11px] font-bold text-[#006699] underline hover:bg-[#dfe6ef]',
            homeLink: 'mt-0.5 whitespace-nowrap border-t border-[#006699]/40 px-2 py-1 pt-1.5 text-[11px] text-[#006699] underline hover:bg-[#dfe6ef]',
          }} includeMsgType="forum" isMsgOnly />
        </>
      )}
      <span className="ml-auto flex flex-none items-center gap-1">
        {/* POSSESSION-MODEL: desk state before the identity; identity keeps
            the bar's extreme right (= page top-right). */}
        <DeskStateBadge skin={{
          root: 'flex items-center whitespace-nowrap px-2 text-[11px] font-bold',
          possessedOpen: 'text-[#006699]',
          possessedPin: 'text-[#006699]',
          locked: 'text-[#8a97a5]',
          label: 'cursor-pointer border-0 bg-transparent p-0 text-inherit underline decoration-dotted underline-offset-2',
          form: 'ml-1.5 inline-flex items-center gap-1',
          input: 'h-[24px] w-[100px] border border-[#006699] bg-white px-1.5 text-[11px] text-black outline-none',
          button: 'h-[24px] cursor-pointer border border-[#006699] bg-white px-1.5 text-[11px] font-bold text-[#006699] hover:bg-[#dfe6ef]',
        }} />
        <AuthEntry skin={{
          root: 'flex items-center gap-1',
          signInButton: 'h-7 flex-none border border-[#006699] bg-white px-2 font-bold hover:bg-[#dfe6ef]',
          identity: 'flex items-center gap-1',
          identityLink: 'font-bold text-[#006699] hover:underline max-w-[110px] overflow-hidden text-ellipsis whitespace-nowrap',
          signOutButton: 'h-7 flex-none border border-[#006699] bg-white px-2 hover:bg-[#dfe6ef]',
          membershipButton: 'h-7 flex-none border border-[#006699] bg-[#ffffcc] px-2 font-bold hover:bg-[#fff1a6]',
          membershipStatus: 'flex h-7 flex-none items-center bg-[#eef3f8] px-2 font-bold',
          membershipError: 'flex h-7 flex-none items-center px-2 text-[#8b0000]',
        }} />
      </span>
    </nav>
  )
}
